import { Box, Grid, Typography } from '@mui/material';
import { makeStyles } from '@mui/styles';
import type { AddressBookRecord } from '@tassat/fred-definitions';
import { map, orderBy, uniq } from 'lodash';
import React from 'react';
import ReactGA from 'react-ga4';

import { ApplicationMuiTheme } from 'Lib/themes/muiTheme';

import ClientSelect from './clientSelect';
import type {
    MappedOption, Option, SearchParameters, SelectOption
} from './types';

const useClientSearchStyles = makeStyles((theme: ApplicationMuiTheme) => {
    return {
        fieldLabel: {
            lineHeight: '50px',
            fontWeight: 700,
            color: theme?.app?.applicationColors?.fontLabelColor || '#727272'
        },
        clearSearch: {
            lineHeight: '50px',
            fontWeight: 700,
            color: theme?.app?.clientTheme.colors.primaryColor || '#375073',
            textDecoration: 'underline',
            cursor: 'pointer'
        },
        clientField: { height: '20px' }
    };
});

type ClientSearchProps = {
    data: AddressBookRecord[];
    onSearch: (values: SearchParameters) => unknown;
};

const ClientSearch: React.FC<ClientSearchProps> = ({ data, onSearch }) => {
    const classes = useClientSearchStyles();
    const INITIAL_VALUE: SelectOption = {
        value: '',
        groupedBy: '',
        label: ''
    };
    const [ selectedClient, setSelectedClient ] = React.useState<SelectOption | null>(INITIAL_VALUE);

    React.useEffect((): void => {
        onSearch({
            client: {
                value: selectedClient.value,
                groupedBy: selectedClient.groupedBy
            }
        });
    }, [selectedClient]);

    const dropdownOptions = React.useMemo((): Option[] => {
        const options: Option[] = [
            {
                label: 'Firm Name',
                options: []
            },
            {
                label: 'Account Name',
                options: []
            }
        ];
        const mappedOptions: MappedOption = {
            firmNames: [],
            walletNames: []
        };

        for (const ele of data) {
            mappedOptions.firmNames.push(ele.FirmName);

            if (ele.NickName) {
                mappedOptions.walletNames.push(ele.NickName);
            } else {
                mappedOptions.walletNames.push(`${ele.AccountName}`);
            }
        }

        options[0].options = map(
            orderBy(uniq(mappedOptions.firmNames), [name => name.toLowerCase()], ['asc']),
            (val): SelectOption => ({
                value: val,
                label: val,
                groupedBy: 'firmName'
            })
        );
        options[1].options = map(orderBy(
            uniq(mappedOptions.walletNames), [name => name.toLowerCase()], ['asc']
        ),
        (val): SelectOption => ({
            value: val,
            label: val,
            groupedBy: 'walletName'
        }));

        return options;
    }, [data]);

    const handleClientChange = (value: SelectOption): void => {
        ReactGA.event('click', {
            event_category: 'engagement',
            event_label: 'address_book_search'
        });
        setSelectedClient(value);
    };

    const handleSearchClear = (): void => {
        ReactGA.event('click', {
            event_category: 'engagement',
            event_label: 'address_book_clear_search'
        });
        if (selectedClient.value) {
            setSelectedClient(INITIAL_VALUE);
        }
    };

    return (
        <Grid
            alignContent='center'
            container
        >
            <Grid item>
                <Typography
                    className={classes.fieldLabel}
                    variant='body1'
                >Client Look-Up
                </Typography>
            </Grid>
            <Box mr={1} />
            <Grid
                item
                xs={5}
            >
                <ClientSelect
                    onChange={handleClientChange}
                    options={dropdownOptions}
                    value={selectedClient}
                />
            </Grid>
            <Box mr={2} />
            <Grid item>
                <Typography
                    className={classes.clearSearch}
                    onClick={handleSearchClear}
                    variant='body1'
                >
                    Clear Search
                </Typography>
            </Grid>
        </Grid>
    );
};

export default ClientSearch;